import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback, useState } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--alexstrasza/ai-powered-risk-assessment-spotlights';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import {
	RiskAssessmentSpotlightTour,
	tour,
} from '../../../components/risk-assessment-spotlight-tour';

import { type MessageTemplateContext } from '../../../index';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

type RenderProps = MessageTemplateContext & PlacementRenderProps;

export const render: {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (input: PlacementRenderProps & Record<string, never>) => JSX.Element | null;
} = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: ({ onDismiss }: RenderProps) => {
		const { messageDismissed } = useMessageEvent();
		const { onClosed, onAcknowledged, onStepChanged } = usePostOfficeUserInteractions();
		const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

		const selectors = Object.keys(tour);
		const totalSteps = selectors.length;
		const [stepIndex, setStepIndex] = useState(0);
		const selector = selectors[stepIndex];

		const renderInline = useMountToHtmlElement({
			selectors: selector ?? '',
			as: 'inline',
			position: 'inside',
		});

		const dismiss = useCallback(() => {
			void messageDismissed();
			void onClosed({ interactionIdentifier: 'tour-dismissed', additionalMetadata: { stepIndex } });
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'riskAssessmentSpotlightDismiss',
				attributes: { stepIndex },
			});
			onDismiss?.();
		}, [messageDismissed, onClosed, fireAnalyticsEvent, onDismiss, stepIndex]);

		const next = useCallback(() => {
			void onStepChanged({
				interactionIdentifier: 'next-step',
				direction: 'next',
				fromStep: stepIndex,
				toStep: stepIndex + 1,
			});
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'riskAssessmentSpotlightNextStep',
				attributes: { fromStep: stepIndex, toStep: stepIndex + 1 },
			});
			setStepIndex((current) => Math.min(current + 1, totalSteps - 1));
		}, [onStepChanged, fireAnalyticsEvent, stepIndex, totalSteps]);

		const done = useCallback(() => {
			void messageDismissed();
			void onAcknowledged({
				interactionIdentifier: 'tour-complete',
				additionalMetadata: { tourCompleted: true },
			});
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'riskAssessmentSpotlightDone',
			});
			onDismiss?.();
		}, [messageDismissed, onAcknowledged, fireAnalyticsEvent, onDismiss]);

		return (
			<div>
				{renderInline(
					<RiskAssessmentSpotlightTour
						dismiss={dismiss}
						next={next}
						done={done}
						stepIndex={stepIndex}
						totalSteps={totalSteps}
					/>,
				)}
			</div>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
